import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  ButtonBase,
  Collapse,
  Dialog,
  DialogContent,
  Grow,
  IconButton,
  Stack,
  Switch,
  Typography,
  useMediaQuery
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { CloseOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

import useAuth from 'hooks/useAuth';
import { useBillingClient } from 'client/client-hook';
import Button from '../../components/ui/Button';
import { analytics } from 'lib/analytics';
import { inkButtonSx } from './inkButtonSx';
// @ts-ignore - lib/billing is plain JS
import { CHECKOUT_STEPS } from '../../lib/billing/config';
import { formatCents } from '../account-settings/subscriptions/checkout-v2/format';
import { QtyStepper } from '../account-settings/subscriptions/checkout-v2/Configure/QtyStepper';
import { useBillingStore } from '../account-settings/subscriptions/checkout-v2/store/useBillingStore';
import { useBillingSync } from '../account-settings/subscriptions/checkout-v2/store';
import { useCheckoutStore } from '../account-settings/subscriptions/checkout-v2/store/useCheckoutStore';
import { useUpgradeDrawer } from '../../contexts/UpgradeDrawerContext';
import { useSubscriptionStatus } from '../../hooks/useSubscriptionStatus';
import { useQuickSubscribe } from './useQuickSubscribe';
import { leaveTourForCheckout, useToBilling } from './useStageCheckout';
import {
  MONTHLY,
  QUICK_PRODUCTS,
  QuickOpts,
  QuickTier,
  analyzerCents,
  cfdTierCents,
  cycleOf,
  cyclesForProduct,
  futuresCents,
  futuresIncluded,
  selectedMode
} from './quickProducts';
import { CYCLE_OPTIONS, cycleSuffix } from '../welcome-tour/offerQuote';
import { PlatformLogos } from './platformLogos';
import { TextTabs } from './TextTab';

// Premium wears the marketing site's gold identity (globals.css tc-gold-*).
const GOLD = { main: '#d4a017', mid: '#f5c544', dark: '#b8841a', deep: '#8b5a00', highlight: '#fff3c2', ink: '#3d2a05' };

// What each tier buys — the marketing site's pricing bullets, compressed.
const TIER_PERKS: Record<QuickTier, string> = {
  standard: 'Per-account billing · 6 CFD platforms · cancel anytime',
  premium: 'Everything in Standard · Lower latency for HFT · Priority support'
};

const DISPLAY_FONT = "'Instrument Sans', 'Inter', sans-serif";

/**
 * Quick-subscribe: pick a product, (optionally) size it, pay. The full
 * builder never opens — Continue stages the cart and lands the shared
 * checkout drawer straight on Payment (the DirectBuyMcpModal pattern).
 * Products are data: see quickProducts.ts.
 */
const QuickSubscribeDialog = () => {
  const theme = useTheme();
  const toBilling = useToBilling();
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const { user, isAuthenticated } = useAuth() as { user?: { tcId?: string }; isAuthenticated?: boolean };
  const getBillingClient = useBillingClient();
  const { open, product: productKey, close } = useQuickSubscribe();
  const { openUpgradeDrawer } = useUpgradeDrawer();
  const { hadPreviousSub: returning, hasPastDue } = useSubscriptionStatus();
  const navigate = useNavigate();

  // Register the billing client wherever this dialog mounts. MainLayout gets
  // it from CheckoutDrawer anyway (the sync ref-counts), but the welcome-tour
  // route has no other consumer — without this, fetchCatalog no-ops there and
  // Continue never enables.
  useBillingSync({
    enabled: Boolean(isAuthenticated && user?.tcId),
    userId: user?.tcId ?? null,
    clientFactory: getBillingClient
  });

  const catalog = useBillingStore((s) => s.catalog);
  const overview = useBillingStore((s) => s.overview);
  const fetchBilling = useBillingStore((s) => s.fetch);
  const fetchCatalog = useBillingStore((s) => s.fetchCatalog);
  useEffect(() => {
    if (open && user?.tcId) {
      fetchCatalog(user.tcId);
      // Overview carries trialUsedProductIds — needed to show the trial badge
      // honestly instead of promising a trial payment will deny.
      fetchBilling(user.tcId);
    }
  }, [open, user?.tcId, fetchCatalog, fetchBilling]);

  const trialUsedProductIds = useMemo<string[]>(() => ((overview as any)?.trialUsedProductIds as string[]) || [], [overview]);
  // Two, not one: a copier links a master to a slave, so a single-account
  // licence buys something that physically cannot copy.
  const [baseOpts, setOpts] = useState<Omit<QuickOpts, 'trialUsedProductIds'>>({ tier: 'standard', accounts: 2 });
  const rawOpts: QuickOpts = useMemo(() => ({ ...baseOpts, trialUsedProductIds }), [baseOpts, trialUsedProductIds]);
  const presetAnalyzer = useQuickSubscribe((s) => s.presetAnalyzer);
  const presetTier = useQuickSubscribe((s) => s.presetTier);
  // Funnel visibility: the modal is the money surface, so opens and true
  // dismissals are events. stageAndPay and "Compare all plans" close through
  // paths of their own and are already tracked — only the X/backdrop counts
  // as a dismissal.
  useEffect(() => {
    if (open) analytics.quickSubscribeViewed({ product: useQuickSubscribe.getState().product });
  }, [open]);
  const dismiss = () => {
    analytics.quickSubscribeDismissed({ product: useQuickSubscribe.getState().product });
    close();
  };
  const presetMode = useQuickSubscribe((s) => s.presetMode);
  const presetCycle = useQuickSubscribe((s) => s.presetCycle);
  const presetAccounts = useQuickSubscribe((s) => s.presetAccounts);
  const presetFuturesExtra = useQuickSubscribe((s) => s.presetFuturesExtra);
  useEffect(() => {
    if (open)
      setOpts({
        tier: presetTier ?? 'standard',
        accounts: presetAccounts ?? 2,
        futuresExtra: presetFuturesExtra ?? 0,
        analyzer: presetAnalyzer,
        // The tour's ticket carries the billing cycle the user picked.
        cycle: presetCycle ?? MONTHLY,
        // Mode presets key by product (QuickOpts.modes), so a futures
        // 'unlimited' entry point opens with that pill already selected.
        modes: presetMode ? { [useQuickSubscribe.getState().product]: presetMode } : undefined
      });
  }, [open, presetAnalyzer, presetTier, presetMode, presetCycle, presetAccounts, presetFuturesExtra]);

  const dark = theme.palette.mode === 'dark';
  const primary = theme.palette.primary.main;
  const selected = QUICK_PRODUCTS.find((p) => p.key === productKey) ?? QUICK_PRODUCTS[0];
  // Billing cycle: the four the checkout sells; a product that is not sold on
  // the chosen one (dedicated environments) falls back to monthly.
  const cycleChoices = cyclesForProduct(
    selected.key,
    CYCLE_OPTIONS.map((c) => c.id)
  );
  const cycle = cycleChoices.includes(cycleOf(rawOpts)) ? cycleOf(rawOpts) : MONTHLY;
  const opts: QuickOpts = useMemo(() => ({ ...rawOpts, cycle }), [rawOpts, cycle]);
  const suffix = cycleSuffix(cycle);
  const analyzerApplies = (selected.key === 'cfd' || selected.key === 'futures' || selected.key === 'both') && Boolean(opts.analyzer);
  const totalCents = selected.price(catalog, opts) + (analyzerApplies ? analyzerCents(catalog, cycle) : 0);
  // A trial cart charges nothing today; saying "$30/mo" on the button at that
  // moment reads as an immediate charge and is the wrong number either way.
  const trialNow = Boolean(selected.badge?.(catalog, opts));

  const stageAndPay = () => {
    if (hasPastDue) {
      // An overdue subscription can't be re-bought — staging the same plan
      // resolves to zero changes and errors at payment. Settle it instead.
      analytics.getStartedCtaClicked({ step: 'subscribe', source: 'quick_subscribe', past_due: true });
      close();
      toBilling(false);
      return;
    }
    analytics.getStartedCtaClicked({
      step: 'subscribe',
      plan: selected.key === 'cfd' ? `cfd-${opts.tier}` : selected.key,
      source: 'quick_subscribe',
      returning
    });
    const store = useCheckoutStore.getState();
    // Hand the cart to CheckoutProvider as a one-shot patch instead of
    // writing the selection directly: hydration from active subscriptions
    // replaces the whole selection on drawer open, which silently wiped a
    // directly-staged cart for anyone who already owned an add-on or env.
    // The provider applies this patch ON TOP of whatever it hydrates.
    store.setPendingQuickPatch({ ...selected.stage(catalog, opts), ...(analyzerApplies ? { analyzerEnabled: true } : null) });
    // Attribute the eventual purchase to this surface.
    useQuickSubscribe.getState().markEntry('quick_subscribe');
    store.openDrawer(CHECKOUT_STEPS.payment);
    close();
    leaveTourForCheckout(navigate);
  };

  const MONO_FONT = "'IBM Plex Mono', 'JetBrains Mono', ui-monospace, monospace";
  const sectionSx = {
    fontFamily: MONO_FONT,
    fontSize: '0.6rem',
    letterSpacing: '.16em',
    textTransform: 'uppercase' as const,
    color: 'text.secondary',
    whiteSpace: 'nowrap' as const
  };
  // Dedicated environments pick an environment, everything else a mode; the
  // gutter only fits the short form.
  const modeLabel = (key: string) => (key === 'dedicated' ? { short: 'Env', full: 'Environment' } : { short: 'Mode', full: 'Mode' });
  const termRow = (label: string, children: React.ReactNode) => (
    <Stack direction="row" spacing={1.5} alignItems="baseline" useFlexGap flexWrap="wrap">
      <Box sx={{ width: 64, ...sectionSx }}>{label}</Box>
      {children}
    </Stack>
  );
  const countRow = (
    label: string,
    hint: string,
    value: number,
    min: number,
    onChange: (v: number) => void,
    ariaLabel: string,
    prefix?: string
  ) => (
    <Stack direction="row" spacing={1.5} alignItems="center" useFlexGap flexWrap="wrap">
      <Box sx={{ width: 64, ...sectionSx }}>{label}</Box>
      <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
        {prefix && (
          <Typography component="span" sx={{ fontFamily: MONO_FONT, fontSize: '0.68rem', color: 'text.secondary' }}>
            {prefix}
          </Typography>
        )}
        <QtyStepper value={value} onChange={onChange} min={min} ariaLabel={ariaLabel} />
        <Typography component="span" sx={{ fontFamily: MONO_FONT, fontSize: '0.62rem', color: primary, whiteSpace: 'nowrap' }}>
          {hint}
        </Typography>
      </Stack>
    </Stack>
  );
  const money = (cents: number, big = false) => (
    <Typography
      component="span"
      sx={{
        fontFamily: MONO_FONT,
        fontWeight: 600,
        fontSize: big ? '2rem' : '1.05rem',
        letterSpacing: '-.03em',
        fontVariantNumeric: 'tabular-nums',
        lineHeight: 1
      }}
    >
      {formatCents(cents)}
      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.4, fontFamily: DISPLAY_FONT }}>
        {suffix}
      </Typography>
    </Typography>
  );

  return (
    <Dialog
      open={open}
      onClose={dismiss}
      maxWidth="xs"
      fullWidth
      TransitionComponent={Grow}
      transitionDuration={reduceMotion ? 0 : 220}
      PaperProps={{ sx: { borderRadius: '16px', backgroundImage: 'none' } }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 3, pt: 2.25, pb: 1 }}>
        <Typography variant="h4" sx={{ fontFamily: DISPLAY_FONT, fontWeight: 700, letterSpacing: '-0.02em' }}>
          {returning ? 'Welcome back' : 'Start your plan'}
        </Typography>
        <IconButton onClick={dismiss} size="small" aria-label="Close quick subscribe">
          <CloseOutlined />
        </IconButton>
      </Stack>
      <DialogContent sx={{ px: 3, pb: 2.5, pt: 0 }}>
        {/* Billing cycle — the checkout's own four, savings as it states them. */}
        <Box sx={{ pb: 1.25 }}>
          {termRow(
            'Billing',
            <TextTabs
              label="Billing cycle"
              value={cycle}
              onChange={(id) => setOpts((o) => ({ ...o, cycle: id }))}
              options={CYCLE_OPTIONS.filter((c) => cycleChoices.includes(c.id)).map((c) => ({
                key: c.id,
                label: c.label,
                note: c.chip || undefined
              }))}
            />
          )}
        </Box>

        {/* One list, hairlines between rows. The chosen row carries an accent
            bar and opens to its own terms — no cards inside cards. */}
        <Box sx={{ borderTop: `1px solid ${theme.palette.divider}` }}>
          {QUICK_PRODUCTS.map((p) => {
            const isSelected = p.key === selected.key;
            const rowCents = p.price(catalog, opts);
            const badge = p.badge?.(catalog, opts);
            const mode = selectedMode(p, catalog, opts);
            return (
              <Box
                key={p.key}
                sx={{
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  borderLeft: `2px solid ${isSelected ? primary : 'transparent'}`,
                  bgcolor: isSelected ? alpha(primary, dark ? 0.06 : 0.035) : 'transparent',
                  transition: 'background 160ms ease, border-color 160ms ease'
                }}
              >
                <ButtonBase
                  onClick={() => useQuickSubscribe.getState().openQuickSubscribe(p.key as any)}
                  aria-pressed={isSelected}
                  sx={{
                    width: '100%',
                    py: 1.5,
                    pl: 1.75,
                    pr: 1.25,
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    textAlign: 'left',
                    gap: 1.5
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={1} useFlexGap flexWrap="wrap">
                      <Typography sx={{ fontFamily: DISPLAY_FONT, fontWeight: 600, fontSize: 15, opacity: isSelected ? 1 : 0.85 }}>
                        {p.title}
                      </Typography>
                      {badge && (
                        <Typography
                          component="span"
                          sx={{
                            fontFamily: MONO_FONT,
                            fontSize: '0.62rem',
                            letterSpacing: '.06em',
                            color: primary,
                            textTransform: 'uppercase'
                          }}
                        >
                          {badge}
                        </Typography>
                      )}
                    </Stack>
                    {p.platforms ? (
                      <Box sx={{ mt: 0.6 }}>
                        <PlatformLogos items={p.platforms} dark={dark} size={13} />
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        {p.meta}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ pt: 0.25, flexShrink: 0 }}>{money(rowCents)}</Box>
                </ButtonBase>

                {/* The row's terms, only while chosen. */}
                <Collapse in={isSelected} unmountOnExit>
                  <Stack spacing={1} sx={{ pl: 1.75, pr: 1.25, pb: 1.75 }}>
                    {p.modes &&
                      termRow(
                        modeLabel(p.key).short,
                        <TextTabs
                          label={modeLabel(p.key).full}
                          value={mode}
                          onChange={(key) => setOpts((o) => ({ ...o, modes: { ...o.modes, [p.key]: key } }))}
                          options={p
                            .modes(catalog, opts)
                            .filter((m) => m.cents != null)
                            .map((m) => ({ key: m.key, label: m.label, note: `${formatCents(m.cents as number)}${suffix} · ${m.detail}` }))}
                        />
                      )}
                    {p.configurable && (
                      <>
                        {termRow(
                          'Tier',
                          <TextTabs
                            label="Tier"
                            value={opts.tier}
                            onChange={(t) => setOpts((o) => ({ ...o, tier: t }))}
                            options={(['standard', 'premium'] as QuickTier[]).map((t) => ({
                              key: t,
                              label: t === 'standard' ? 'Standard' : 'Premium',
                              note: `${formatCents(cfdTierCents(catalog, t, cycle))}/acct`
                            }))}
                          />
                        )}
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            pl: '76px',
                            color: opts.tier === 'premium' ? (dark ? alpha(GOLD.mid, 0.9) : GOLD.deep) : 'text.secondary'
                          }}
                        >
                          {TIER_PERKS[opts.tier]}
                        </Typography>
                        {countRow(
                          'Accounts',
                          'one to copy from, one to copy to',
                          Math.max(2, opts.accounts),
                          2,
                          (v: number) => setOpts((o) => ({ ...o, accounts: Math.max(2, v) })),
                          'Trading accounts'
                        )}
                      </>
                    )}
                    {p.key === 'both' &&
                      countRow(
                        'CFD',
                        `${formatCents(cfdTierCents(catalog, 'standard', cycle))}/acct`,
                        Math.max(1, opts.accounts),
                        1,
                        (v: number) => setOpts((o) => ({ ...o, accounts: Math.max(1, v) })),
                        'CFD accounts'
                      )}
                    {(p.key === 'both' || (p.key === 'futures' && mode !== 'unlimited')) &&
                      countRow(
                        'Futures',
                        `${formatCents(futuresCents(catalog, 1, cycle) - futuresCents(catalog, 0, cycle))}/extra`,
                        opts.futuresExtra ?? 0,
                        0,
                        (v: number) => setOpts((o) => ({ ...o, futuresExtra: Math.max(0, v) })),
                        'Extra futures accounts',
                        `${futuresIncluded(catalog, cycle)} included +`
                      )}
                  </Stack>
                </Collapse>
              </Box>
            );
          })}

          {/* Analyzer rides along on a copier cart (it requires copier accounts,
              so it can't be first purchase on its own) — one checkout, no second
              trip through the builder. */}
          {(selected.key === 'cfd' || selected.key === 'futures' || selected.key === 'both') && (
            <Stack
              component="label"
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1.5}
              sx={{ py: 1.25, pl: 1.75, pr: 0.5, borderBottom: `1px solid ${theme.palette.divider}`, cursor: 'pointer' }}
            >
              <Box>
                <Typography sx={{ fontFamily: DISPLAY_FONT, fontWeight: 600, fontSize: 13.5 }}>Add Analyzer</Typography>
                <Typography variant="caption" color="text.secondary">
                  Win rate, drawdown & equity curves · MT4 & MT5
                </Typography>
              </Box>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography
                  sx={{
                    fontFamily: MONO_FONT,
                    fontWeight: 600,
                    fontSize: 13,
                    fontVariantNumeric: 'tabular-nums',
                    color: opts.analyzer ? primary : 'text.secondary'
                  }}
                >
                  +{formatCents(analyzerCents(catalog, cycle))}
                  {suffix}
                </Typography>
                <Switch
                  size="small"
                  checked={Boolean(opts.analyzer)}
                  onChange={(_, on) => setOpts((o) => ({ ...o, analyzer: on }))}
                  inputProps={{ 'aria-label': 'Add Analyzer' }}
                />
              </Stack>
            </Stack>
          )}
        </Box>

        <Box
          sx={{
            // Short phones scroll the product list; the money button and its
            // price context stay pinned to the dialog's bottom edge.
            position: { xs: 'sticky', sm: 'static' },
            bottom: 0,
            zIndex: 1,
            mx: { xs: -3, sm: 0 },
            px: { xs: 3, sm: 0 },
            pb: { xs: 0.5, sm: 0 },
            bgcolor: { xs: 'background.paper', sm: 'transparent' }
          }}
        >
          {/* The total, printed like the ticket's. */}
          <Stack direction="row" alignItems="flex-end" justifyContent="space-between" sx={{ pt: 1.75, pb: 0.25 }}>
            <Box>
              <Typography sx={{ fontFamily: DISPLAY_FONT, fontWeight: 600, fontSize: 13 }}>
                {trialNow ? 'Due today' : 'Due today'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {trialNow ? `10-day free trial, then ${formatCents(totalCents)}${suffix}` : `then ${formatCents(totalCents)}${suffix}`}
              </Typography>
            </Box>
            {catalog ? (
              money(trialNow ? 0 : totalCents, true)
            ) : (
              <Typography variant="caption" color="text.secondary">
                Loading prices…
              </Typography>
            )}
          </Stack>
          <Button
            fullWidth
            variant="contained"
            size="large"
            disabled={!catalog}
            onClick={stageAndPay}
            sx={{
              py: 1.3,
              mt: 1.25,
              minHeight: 44,
              // The funnel's commit buttons are ink with a machined edge.
              ...inkButtonSx(dark)
            }}
          >
            {!catalog
              ? 'Loading prices…'
              : hasPastDue
              ? 'Fix payment on Billing'
              : trialNow
              ? 'Start free trial — $0 today'
              : `Continue — ${formatCents(totalCents)} due today`}
          </Button>
          {!hasPastDue && (
            <Typography variant="caption" color="text.secondary" display="block" textAlign="center" sx={{ mt: 0.75 }}>
              {trialNow
                ? 'Card required to start — cancel before the trial ends and you pay nothing.'
                : "Next: card details — you'll see the exact total before anything is charged."}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" display="block" textAlign="center" sx={{ mt: 1 }}>
            Cancel anytime ·{' '}
            <Box
              component="button"
              type="button"
              onClick={() => {
                // Carry the cart across instead of dropping it — switching to the
                // full builder used to silently discard everything just chosen.
                useCheckoutStore
                  .getState()
                  .setPendingQuickPatch({ ...selected.stage(catalog, opts), ...(analyzerApplies ? { analyzerEnabled: true } : null) });
                close();
                openUpgradeDrawer(null);
              }}
              sx={{ all: 'unset', cursor: 'pointer', textDecoration: 'underline', font: 'inherit', color: 'inherit' }}
            >
              Compare all plans
            </Box>
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default QuickSubscribeDialog;
